import { useEffect, useMemo, useState } from 'react';
import { ImagePlus } from 'lucide-react';
import {
  useAboutUsSettings,
  useContactSettings,
  useHomePageSettings,
  useUpdateAboutUsSettings,
  useUpdateContactSettings,
  useUpdateHomePageSettings,
} from '@/hooks/useSettings';
import { useNewsList } from '@/hooks/useNews';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { settingsApi } from '@/api/settings';
import { notifyError, notifySuccess } from '@/lib/feedback';

type AboutUsCoverMode = 'url' | 'upload';

const formatDate = (value?: string) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('id-ID');
};

export function SettingsPage() {
  const { data: contactData, isLoading: isContactLoading } = useContactSettings();
  const { data: aboutUsData, isLoading: isAboutUsLoading } = useAboutUsSettings();
  const { data: sliderData, isLoading: isSliderLoading } = useHomePageSettings();
  const { data: newsData, isLoading: isNewsLoading } = useNewsList({ page: 1, limit: 100 });
  const updateContact = useUpdateContactSettings();
  const updateAboutUs = useUpdateAboutUsSettings();
  const updateSlider = useUpdateHomePageSettings();

  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAlamat] = useState('');
  const [aboutUsLabel, setAboutUsLabel] = useState('');
  const [aboutUsTitle, setAboutUsTitle] = useState('');
  const [aboutUsDescription, setAboutUsDescription] = useState('');
  const [aboutUsCoverImage, setAboutUsCoverImage] = useState('');
  const [aboutUsCoverMode, setAboutUsCoverMode] = useState<AboutUsCoverMode>('url');
  const [aboutUsCoverFile, setAboutUsCoverFile] = useState<File | null>(null);
  const [aboutUsCoverPreview, setAboutUsCoverPreview] = useState('');
  const [isUploadingAboutUsCover, setIsUploadingAboutUsCover] = useState(false);
  const [selectedNewsIds, setSelectedNewsIds] = useState<number[]>([]);
  const [intervalValue, setIntervalValue] = useState('5');
  const [intervalUnit, setIntervalUnit] = useState<'seconds' | 'ms'>('seconds');

  useEffect(() => {
    setPhone(contactData?.phone || '');
    setEmail(contactData?.email || '');
    setAlamat(contactData?.address || '');
  }, [contactData]);

  useEffect(() => {
    setAboutUsLabel(aboutUsData?.label || 'Tentang Kami');
    setAboutUsTitle(aboutUsData?.title || 'Membangun Kebaikan Bersama Masyarakat');
    setAboutUsDescription(aboutUsData?.description || aboutUsData?.content || '');
    setAboutUsCoverImage(aboutUsData?.coverImage || '');
    setAboutUsCoverPreview(aboutUsData?.coverImage || '');
    setAboutUsCoverFile(null);
    setAboutUsCoverMode((aboutUsData?.coverImage || '').includes('/uploads/') ? 'upload' : 'url');
  }, [aboutUsData]);

  useEffect(() => {
    setSelectedNewsIds(sliderData?.newsIds || []);
    setIntervalValue(String(sliderData?.intervalValue ?? 5));
    setIntervalUnit(sliderData?.intervalUnit === 'ms' ? 'ms' : 'seconds');
  }, [sliderData]);

  const selectedCount = selectedNewsIds.length;
  const availableNews = newsData?.data || [];
  const allSelected = availableNews.length > 0 && availableNews.every((item) => selectedNewsIds.includes(item.id));
  const sliderAmount = selectedCount;

  const sortedNews = useMemo(() => {
    return [...availableNews].sort((a, b) => {
      const left = a.tanggal || '';
      const right = b.tanggal || '';
      return right.localeCompare(left);
    });
  }, [availableNews]);

  const onSimpanKontak = async () => {
    await updateContact.mutateAsync({ phone, email, address });
  };

  const onSimpanAboutUs = async () => {
    try {
      let coverImage = aboutUsCoverMode === 'url' ? aboutUsCoverImage.trim() : aboutUsCoverImage;

      if (aboutUsCoverMode === 'upload' && aboutUsCoverFile) {
        setIsUploadingAboutUsCover(true);
        const uploadResult = await settingsApi.uploadAboutUsImage(aboutUsCoverFile);
        coverImage = uploadResult.url;
        setAboutUsCoverImage(uploadResult.url);
        setAboutUsCoverPreview(uploadResult.url);
        setAboutUsCoverFile(null);
        notifySuccess('Cover About Us berhasil diunggah');
      }

      await updateAboutUs.mutateAsync({
        label: aboutUsLabel,
        title: aboutUsTitle,
        description: aboutUsDescription,
        content: aboutUsDescription,
        coverImage,
      });
    } catch (error) {
      notifyError('Gagal menyimpan About Us', error, false);
    } finally {
      setIsUploadingAboutUsCover(false);
    }
  };

  const onPickAboutUsCover = (file?: File) => {
    if (!file) return;

    setAboutUsCoverFile(file);
    setAboutUsCoverPreview(URL.createObjectURL(file));
  };

  const toggleNews = (newsId: number, checked: boolean) => {
    setSelectedNewsIds((prev) => {
      if (checked) {
        return prev.includes(newsId) ? prev : [...prev, newsId];
      }

      return prev.filter((id) => id !== newsId);
    });
  };

  const toggleAllNews = (checked: boolean) => {
    setSelectedNewsIds(checked ? availableNews.map((item) => item.id) : []);
  };

  const onSimpanSlider = async () => {
    await updateSlider.mutateAsync({
      amount: sliderAmount,
      intervalValue: Math.max(1, Number(intervalValue) || 1),
      intervalUnit,
      newsIds: selectedNewsIds,
      news: [],
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold tracking-tight">Settings</h2>
        <p className="text-muted-foreground">Konfigurasi kontak publik, About Us, dan slider homepage.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Pengaturan Kontak</CardTitle>
          <CardDescription>Dipakai untuk informasi publik di frontend.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="phone">Nomor Telepon</Label>
            <Input id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="08xxxxxxxxxx" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@example.com" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="address">Alamat</Label>
            <Input id="address" value={address} onChange={(e) => setAlamat(e.target.value)} placeholder="Alamat..." />
          </div>
          <Button onClick={onSimpanKontak} disabled={isContactLoading || updateContact.isPending}>
            {updateContact.isPending ? 'Menyimpan...' : 'Simpan Settings Kontak'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>About Us</CardTitle>
          <CardDescription>
            Atur konten dan cover image untuk section About Us pada beranda client.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="about-us-label">Label</Label>
            <Input
              id="about-us-label"
              value={aboutUsLabel}
              onChange={(e) => setAboutUsLabel(e.target.value)}
              placeholder="Tentang Kami"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="about-us-title">Title</Label>
            <Textarea
              id="about-us-title"
              value={aboutUsTitle}
              onChange={(e) => setAboutUsTitle(e.target.value)}
              placeholder="Membangun Kebaikan Bersama Masyarakat"
              className="min-h-[96px]"
            />
          </div>
          <div className="space-y-2">
            <Label>Cover Image Source</Label>
            <Tabs value={aboutUsCoverMode} onValueChange={(value) => setAboutUsCoverMode(value as AboutUsCoverMode)}>
              <TabsList>
                <TabsTrigger value="url">URL</TabsTrigger>
                <TabsTrigger value="upload">Upload</TabsTrigger>
              </TabsList>
              <TabsContent value="url" className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="about-us-cover-image">Cover Image URL</Label>
                  <Input
                    id="about-us-cover-image"
                    value={aboutUsCoverImage}
                    onChange={(e) => {
                      setAboutUsCoverImage(e.target.value);
                      setAboutUsCoverPreview(e.target.value);
                    }}
                    placeholder="https://example.com/about-us-cover.jpg"
                  />
                </div>
              </TabsContent>
              <TabsContent value="upload" className="space-y-3">
                <div className="flex items-center gap-3">
                  <Label htmlFor="about-us-cover-upload" className="cursor-pointer">
                    <span className="inline-flex items-center rounded-md border px-3 py-2 text-sm">
                      <ImagePlus className="mr-2 h-4 w-4" />
                      Pilih Gambar
                    </span>
                  </Label>
                  <Input
                    id="about-us-cover-upload"
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp,image/gif,image/svg+xml"
                    className="hidden"
                    onChange={(e) => onPickAboutUsCover(e.target.files?.[0])}
                  />
                  <p className="text-sm text-muted-foreground">
                    {aboutUsCoverFile ? aboutUsCoverFile.name : aboutUsCoverImage ? 'Menggunakan gambar tersimpan saat ini.' : 'Belum ada file dipilih.'}
                  </p>
                </div>
              </TabsContent>
            </Tabs>
          </div>
          {aboutUsCoverPreview ? (
            <div className="space-y-2">
              <Label>Preview</Label>
              <div className="overflow-hidden rounded-lg border bg-muted/20">
                <img
                  src={aboutUsCoverPreview}
                  alt="About Us cover preview"
                  className="h-56 w-full object-cover"
                />
              </div>
            </div>
          ) : null}
          <div className="space-y-2">
            <Label htmlFor="about-us-content">Description</Label>
            <Textarea
              id="about-us-content"
              value={aboutUsDescription}
              onChange={(e) => setAboutUsDescription(e.target.value)}
              placeholder="SMALA BAIK 89 adalah perkumpulan yang berdedikasi..."
              className="min-h-[160px]"
            />
          </div>
          <Button onClick={onSimpanAboutUs} disabled={isAboutUsLoading || updateAboutUs.isPending || isUploadingAboutUsCover}>
            {updateAboutUs.isPending || isUploadingAboutUsCover ? 'Menyimpan...' : 'Simpan About Us'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Slider Settings</CardTitle>
          <CardDescription>
            Pilih berita yang akan tampil di slider homepage dan atur interval perpindahannya.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-[180px_minmax(0,1fr)_180px]">
            <div className="space-y-2">
              <Label htmlFor="slider-interval-value">Interval</Label>
              <Input
                id="slider-interval-value"
                type="number"
                min={1}
                value={intervalValue}
                onChange={(e) => setIntervalValue(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="slider-interval-unit">Satuan</Label>
              <Select value={intervalUnit} onValueChange={(value) => setIntervalUnit(value as 'seconds' | 'ms')}>
                <SelectTrigger id="slider-interval-unit">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="seconds">Seconds</SelectItem>
                  <SelectItem value="ms">Milliseconds</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Jumlah Slide</Label>
              <div className="flex h-10 items-center rounded-md border px-3 text-sm font-medium">
                {sliderAmount} berita dipilih
              </div>
            </div>
          </div>

          <div className="overflow-hidden rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox checked={allSelected} onCheckedChange={(checked) => toggleAllNews(Boolean(checked))} />
                  </TableHead>
                  <TableHead>Judul Berita</TableHead>
                  <TableHead>Added Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isNewsLoading || isSliderLoading ? (
                  <TableRow>
                    <TableCell colSpan={3} className="py-8 text-center">Memuat...</TableCell>
                  </TableRow>
                ) : sortedNews.length > 0 ? (
                  sortedNews.map((news) => (
                    <TableRow key={news.id} className={selectedNewsIds.includes(news.id) ? 'bg-muted/30' : undefined}>
                      <TableCell>
                        <Checkbox
                          checked={selectedNewsIds.includes(news.id)}
                          onCheckedChange={(checked) => toggleNews(news.id, Boolean(checked))}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{news.judul}</TableCell>
                      <TableCell>{formatDate(news.createdAt || news.tanggal)}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} className="py-8 text-center">Belum ada berita untuk dipilih.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">
              Hanya satuan `seconds` dan `ms` yang bisa dipilih. Urutan slider mengikuti urutan berita yang dipilih saat ini.
            </p>
            <Button
              onClick={onSimpanSlider}
              disabled={updateSlider.isPending || selectedNewsIds.length === 0}
            >
              {updateSlider.isPending ? 'Menyimpan...' : 'Simpan Slider Settings'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
